/**
 * use-property-edit.ts — D-050-06 mixed-by-field-type commit cadence
 * wrapper around `useDraftStore.setDraft`. The hook returns three
 * primitives that the per-row dispatcher (`property-row.tsx`) selects
 * from based on field kind:
 *
 *   - `commit(v)`          → debounced 200ms (text / number / class)
 *   - `commitInstant(v)`   → no debounce (boolean / select / radio /
 *                            color swatch click)
 *   - `commitOnBlur()`     → flush any pending debounce (color drag
 *                            settle / explicit blur)
 *
 * Per-cell debounce: the timer ref is per-hook-instance, and each
 * PropertyRow gets its own hook instance — so cell-switching naturally
 * cancels because the previous row unmounts (`useEffect` cleanup fires
 * + flushes any pending value so the operator's last edit isn't lost).
 *
 * Hand-rolled per CONTEXT D-050-06 Claude's Discretion (no `use-debounce`
 * dep). Pattern mirrors `copyable-id.tsx:8-14` (`useRef<ReturnType<typeof
 * setTimeout>>` precedent — the only `setTimeout` consumer in the inspector
 * tree) + Phase 049 imperative `useDraftStore.getState().setDraft(...)`
 * idiom.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useDraftStore } from '@/features/preview/store/draft-store'

const DEBOUNCE_MS = 200 // D-050-06 debounced cadence window

interface UsePropertyEditApi {
  commit: (value: unknown) => void
  commitInstant: (value: unknown) => void
  commitOnBlur: () => void
}

export function usePropertyEdit(
  blockId: string,
  propertyKey: string,
): UsePropertyEditApi {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingValueRef = useRef<unknown>(undefined)

  useEffect(() => {
    // Cleanup on unmount or on blockId/propertyKey change — flush any
    // pending value so cell-switch never silently drops the operator's
    // last edit (CADENCE-04 + CADENCE-06).
    return () => {
      if (
        timerRef.current !== null &&
        pendingValueRef.current !== undefined
      ) {
        useDraftStore.getState().setDraft(
          blockId,
          propertyKey,
          pendingValueRef.current,
        )
      }
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      pendingValueRef.current = undefined
    }
  }, [blockId, propertyKey])

  const commit = useCallback(
    (value: unknown) => {
      pendingValueRef.current = value
      if (timerRef.current !== null) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        useDraftStore.getState().setDraft(blockId, propertyKey, value)
        timerRef.current = null
        pendingValueRef.current = undefined
      }, DEBOUNCE_MS)
    },
    [blockId, propertyKey],
  )

  const commitInstant = useCallback(
    (value: unknown) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      pendingValueRef.current = undefined
      useDraftStore.getState().setDraft(blockId, propertyKey, value)
    },
    [blockId, propertyKey],
  )

  const commitOnBlur = useCallback(() => {
    if (
      timerRef.current !== null &&
      pendingValueRef.current !== undefined
    ) {
      clearTimeout(timerRef.current)
      useDraftStore.getState().setDraft(
        blockId,
        propertyKey,
        pendingValueRef.current,
      )
      timerRef.current = null
      pendingValueRef.current = undefined
    }
  }, [blockId, propertyKey])

  return { commit, commitInstant, commitOnBlur }
}
